import { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import {
  DatePickerV2,
  FullScreenSpinner,
  TextAreaV2,
  TimePickerV2,
  TimezoneAutocomplete,
} from '@insights-ltd/design-library/components';
import { useAuth } from 'contexts/AuthContext';
import { useBetaEnabled } from 'features';
import {
  getEventDialects,
  isDFCExperience,
  isExploreExperience,
  isIdtlExperience,
} from 'domain/event';
import { FREQUENCY_OPTIONS, Model, PRODUCT_TYPES } from 'variables';
import { LicensedProduct, ProductTypes } from 'types/types';
import { useGetSupportedDialects } from 'api';
import { Box, styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { DateTime } from 'luxon';
import { formatTimeForTimezone } from '@insights-ltd/design-library/utils';
import { TIMEZONE_OPTIONS } from 'domain/timezone';
import {
  getErrorMessage,
  startDateFieldRules,
  startTimeFieldRules,
  endDateFieldRules,
  endTimeFieldRules,
  deadlineFieldRules,
} from './validation';
import { EventDetailsData, EventForm } from './types';
import { Select } from './Select';

type EventDetailsFormProps = {
  event: EventForm;
  onSubmit: (formData: EventDetailsData) => void;
  children: (formState: { isSubmitDisabled: boolean }) => ReactNode;
  invitedCount: number;
  isCreating?: boolean;
};

type UserProducts = Partial<typeof PRODUCT_TYPES>;

const {
  DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE,
  DISCOVERY_FULL_CIRCLE_PROFILE,
  DISCOVERY_SELF_AWARE_PROFILE,
  DISCOVERY_PERSONAL_PROFILE,
  DISCOVERY_EXPLORE_PROFILE,
} = PRODUCT_TYPES;
const CHARACTER_LIMIT = 500;

const useProductTypesForEvent = (
  event: EventForm,
  isCreating: boolean,
): UserProducts => {
  const in3drsBeta = useBetaEnabled('default-3drs');

  if (isIdtlExperience(event.eventType)) {
    return {
      DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE,
    };
  }

  if (isDFCExperience(event.eventType)) {
    return {
      DISCOVERY_FULL_CIRCLE_PROFILE,
    };
  }

  if (isExploreExperience(event.eventType)) {
    return {
      DISCOVERY_EXPLORE_PROFILE,
    };
  }

  if (in3drsBeta && isCreating) {
    return {
      DISCOVERY_SELF_AWARE_PROFILE,
    };
  }

  return {
    DISCOVERY_SELF_AWARE_PROFILE,
    DISCOVERY_PERSONAL_PROFILE,
  };
};

const useGetProductTypesForEvent = (
  event: EventForm,
  userProducts: LicensedProduct[],
  isCreating: boolean,
): Partial<ProductTypes> => {
  const productTypes = useProductTypesForEvent(event, isCreating);

  let productTypesAvailableToUser: UserProducts;
  if (isCreating) {
    productTypesAvailableToUser = Object.fromEntries(
      Object.entries(productTypes).filter(([productKey]) =>
        userProducts.includes(productKey as LicensedProduct),
      ),
    );
  } else {
    productTypesAvailableToUser = Object.fromEntries(
      Object.entries(productTypes).filter(
        ([productKey]) => productKey === event.productType,
      ),
    );
  }

  return productTypesAvailableToUser;
};

const StyledForm = styled('form')(({ theme }) => ({
  '> * + *': {
    marginTop: `${theme.spacing(spacingSizeMap.L)} !important`,
  },
}));

const AVAILABLE_TIMEZONES = TIMEZONE_OPTIONS.map((option) => option.value);

const EventDetailsForm = ({
  event,
  onSubmit,
  children,
  invitedCount,
  isCreating = false,
}: EventDetailsFormProps) => {
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const products = useGetProductTypesForEvent(
    event,
    user?.licensedProducts || [],
    isCreating,
  );

  const translatedAndSortedTimezoneOptions = TIMEZONE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  })).sort((a, b) => a.label.localeCompare(b.label));

  const defaultTimezone =
    isCreating && AVAILABLE_TIMEZONES.includes(detectedTimezone)
      ? detectedTimezone
      : event.timezone;

  const productTypeKeys = Object.keys(products);
  const defaultProductType =
    productTypeKeys.length === 1
      ? (productTypeKeys[0] as LicensedProduct)
      : event.productType;

  let model: Model;
  if (event.eventType === 'INSIGHTS_EXPLORE') {
    model = 'EXPLORE';
  } else if (event.eventType === 'INSIGHTS_DISCOVERY_FULL_CIRCLE') {
    model = 'DFC';
  } else if (
    [
      'INSIGHTS_SELF_AWARE_LEADER',
      'INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS',
    ].includes(event.eventType)
  ) {
    model = 'IDTL';
  } else {
    model = 'ALL_DIALECTS';
  }
  const lang = i18n.language;
  const { data, isPending } = useGetSupportedDialects(model, lang);
  const supportedDialects = data?.dialects;
  const dialects = supportedDialects || getEventDialects(event);

  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
    trigger,
    register,
  } = useForm<EventDetailsData>({
    defaultValues: {
      ...event,
      timezone: defaultTimezone,
      startTime: event.startTime
        ? event.startTime
        : DateTime.fromISO('1970-01-01T09:00:00.000Z', { zone: 'default' }),
      endTime: event.endTime
        ? event.endTime
        : DateTime.fromISO('1970-01-01T17:00:00.000Z', { zone: 'default' }),
      productType: defaultProductType,
      ...(dialects.length === 1 && { dialect: dialects[0].value }),
    },
  });

  const customEmailMessage = useWatch({ control, name: 'customEmailMessage' });
  const isSubmitDisabled = (customEmailMessage?.length ?? 0) > CHARACTER_LIMIT;

  if (isPending) {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }

  return (
    <StyledForm noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Select
        disabled={invitedCount > 0 || productTypeKeys.length === 1}
        id="productType"
        name="productType"
        label={t('ui.event-management.events.create.label.profile-type')}
        control={control}
        error={Boolean(errors.productType)}
        rules={{ required: true }}
        errorMessage={t(
          'ui.event-management.events.create.error.value-required',
        )}
      >
        {Object.entries(products).map(([productType, textKey]) => (
          <MenuItem key={productType} value={productType}>
            {t(textKey)}
          </MenuItem>
        ))}
      </Select>

      <Select
        id="dialect"
        name="dialect"
        label={t('ui.event-management.events.create.label.event-language')}
        control={control}
        disabled={dialects.length === 1}
        error={Boolean(errors.dialect)}
        rules={{ required: true }}
        errorMessage={t(getErrorMessage(errors.dialect))}
      >
        {dialects.map((dialect) => (
          <MenuItem data-dialect key={dialect.value} value={dialect.value}>
            {t(dialect.textKey)}
          </MenuItem>
        ))}
      </Select>
      <Box>
        <Controller
          name="timezone"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <TimezoneAutocomplete
                id="timezone"
                labelText={t(
                  'ui.event-management.events.create.timezone.label-text',
                )}
                placeholder={t(
                  'ui.event-management.events.create.timezone.placeholder',
                )}
                options={translatedAndSortedTimezoneOptions}
                onSelect={(selectedOption) => {
                  if (selectedOption && 'value' in selectedOption) {
                    onChange(selectedOption.value);
                  } else {
                    onChange(null);
                  }
                }}
                selectedValue={value}
                currentTime={formatTimeForTimezone}
                disableClearable
                loading={false} // currently options list of timezones comes from hardcoded list on FE. Keeping this here in case that changes
                loadingText={t(
                  'ui.event-management.events.create.timezone.loading',
                )}
                noOptionsText={t(
                  'ui.event-management.events.create.timezone.nooptions',
                )}
                error={Boolean(error)}
                helperText={
                  error
                    ? t(
                        'ui.event-management.events.create.error.timezone-required',
                      )
                    : t(
                        'ui.event-management.events.create.timezone.helper-text',
                      )
                }
                autocompleteText={t(
                  'ui.event-management.events.create.timezone.info-text',
                )}
                changeText={t(
                  'ui.event-management.events.create.timezone.change-button-text',
                )}
                required
                isCreatingExperience={isCreating}
              />
            );
          }}
        />
      </Box>
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          pt: 1,
        }}
      >
        <Grid size={{ md: 6, xs: 12 }}>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange: onReactHookFormChange, value } }) => {
              return (
                <DatePickerV2
                  disablePast
                  error={Boolean(errors.startDate)}
                  errorMessage={t(getErrorMessage(errors.startDate))}
                  labelText={t(
                    'ui.event-management.events.create.label.start-date',
                  )}
                  onChange={(newValue) => {
                    onReactHookFormChange(newValue);
                    trigger([
                      'startDate',
                      'startTime',
                      'endDate',
                      'endTime',
                      'deadlineAt',
                    ]);
                  }}
                  timezone="default"
                  value={value}
                  sxTextField={{ width: '100%' }}
                />
              );
            }}
            rules={startDateFieldRules}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <Controller
            control={control}
            name="startTime"
            render={({ field: { onChange: onReactHookFormChange, value } }) => {
              return (
                <TimePickerV2
                  data-testid="time-picker-startTime"
                  error={Boolean(errors.startTime)}
                  errorMessage={t(getErrorMessage(errors.startTime))}
                  labelText={t(
                    'ui.event-management.events.create.label.start-time',
                  )}
                  onChange={(newValue) => {
                    onReactHookFormChange(newValue);
                    trigger([
                      'startDate',
                      'startTime',
                      'endDate',
                      'endTime',
                      'deadlineAt',
                    ]);
                  }}
                  timezone="default"
                  value={value}
                  sxTextField={{ width: '100%' }}
                />
              );
            }}
            rules={startTimeFieldRules}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          pt: 1,
        }}
      >
        <Grid size={{ md: 6, xs: 12 }}>
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange: onReactHookFormChange, value } }) => {
              return (
                <DatePickerV2
                  disablePast
                  error={Boolean(errors.endDate)}
                  errorMessage={t(getErrorMessage(errors.endDate))}
                  labelText={t(
                    'ui.event-management.events.create.label.end-date',
                  )}
                  onChange={(newValue) => {
                    onReactHookFormChange(newValue);
                    trigger([
                      'startDate',
                      'startTime',
                      'endDate',
                      'endTime',
                      'deadlineAt',
                    ]);
                  }}
                  timezone="default"
                  value={value}
                  sxTextField={{ width: '100%' }}
                />
              );
            }}
            rules={endDateFieldRules(getValues)}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <Controller
            control={control}
            name="endTime"
            render={({ field: { onChange: onReactHookFormChange, value } }) => {
              return (
                <TimePickerV2
                  data-testid="time-picker-endTime"
                  error={Boolean(errors.endTime)}
                  errorMessage={t(getErrorMessage(errors.endTime))}
                  labelText={t(
                    'ui.event-management.events.create.label.end-time',
                  )}
                  onChange={(newValue) => {
                    onReactHookFormChange(newValue);
                    trigger([
                      'startDate',
                      'startTime',
                      'endDate',
                      'endTime',
                      'deadlineAt',
                    ]);
                  }}
                  timezone="default"
                  value={value}
                  sxTextField={{ width: '100%' }}
                />
              );
            }}
            rules={endTimeFieldRules(getValues)}
          />
        </Grid>
      </Grid>
      {!isIdtlExperience(event.eventType) && (
        <>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ md: 6, xs: 12 }}>
              <Controller
                control={control}
                name="deadlineAt"
                render={({
                  field: { onChange: onReactHookFormChange, value },
                }) => {
                  return (
                    <DatePickerV2
                      disablePast
                      error={Boolean(errors.deadlineAt)}
                      errorMessage={t(getErrorMessage(errors.deadlineAt))}
                      labelText={t(
                        'ui.event-management.events.create.label.evaluator-deadline',
                      )}
                      onChange={(newValue) => {
                        onReactHookFormChange(newValue);
                        trigger([
                          'startDate',
                          'startTime',
                          'endDate',
                          'endTime',
                          'deadlineAt',
                        ]);
                      }}
                      timezone="default"
                      value={value}
                      sxTextField={{ width: '100%' }}
                    />
                  );
                }}
                rules={deadlineFieldRules(getValues)}
              />
            </Grid>
          </Grid>
          <Select
            id="notificationFrequency"
            name="notificationFrequency"
            label={t(
              'ui.event-management.events.create.label.notification-frequency',
            )}
            control={control}
            error={Boolean(errors.notificationFrequency)}
            rules={{ required: true }}
            errorMessage={t(getErrorMessage(errors.notificationFrequency))}
          >
            {FREQUENCY_OPTIONS.map(({ textKey, value }) => (
              <MenuItem key={value} value={value}>
                {t(textKey)}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
      <Controller
        name="customEmailMessage"
        rules={{ maxLength: CHARACTER_LIMIT }}
        control={control}
        render={({ field: { onChange } }) => (
          <TextAreaV2
            id="customEmailMessage"
            label={t('ui.event-management.events.create.label.custom-email')}
            rows={3}
            fullWidth
            maxLength={CHARACTER_LIMIT}
            optionalText={t('ui.event-management.events.optional.field')}
            characterLimitLabelText={t(
              'ui.event-management.generic.text-area.character-label',
            )}
            additionalMessage={t(
              'ui.event-management.events.create.label.custom-email.helper',
            )}
            placeholder={t(
              'ui.event-management.events.create.label.custom-email.placeholder',
            )}
            onChange={onChange}
            slotProps={{ htmlInput: register('customEmailMessage') }}
          />
        )}
      />
      {children({ isSubmitDisabled })}
    </StyledForm>
  );
};

export default EventDetailsForm;
